// Utils/Storage.js
export const STORAGE_KEYS = {
  MY_LAST_SYNC: 'MyLastSyncDT',
  LAST_SYNC: 'A',
  AGENCY_DATA: 'B',
  CONFIG_DATA: 'C',
  NODE_DATA: 'D',
  USER_DATA: 'U',
  USER_CONFIGS_DATA: 'V',
  SCRM_CONFIGS: 'SCRMConfigs',
};

// Master data keys mapping
// export const MASTER_DATA_KEYS = {
//   SOURCES: 'InqSources',
//   CITIES: 'Cities',
//   EXTRA_FIELDS: 'ConnFields',
//   REJECTION_REASONS: 'RejRsns',
//   INACTIVE_REASONS: 'InactRsns',
//   SEGMENTS: 'Segments',
//   APPOINTMENT_TYPES: 'TaskTypes',
//   PRODUCTS: 'Products',
//   CONN_FIELD_PREFS: 'ConnFieldPrefs'  
// };

export const MASTER_DATA_KEYS = {
  sources: 'Sources',
  cities: 'Cities',
  extraFields: 'ConnFields',
  rejectionReasons: 'RejRsns',
  disqlReasons: 'DisqlRsns',
  inactiveReasons: 'InactRsns',
  segments: 'Segments',
  taskTypes: 'TaskTypes',
  products: 'Products',
  threadTypes: 'ThreadTypes',
  recordTypes: 'RecTypes',
  ownEmail: 'OwnEmail',
  ownEmailPwd: 'OwnEmailPwd',
  isOAuthEnabled: 'IsOAuthEnabled',
  contractTypes: 'ContractTypes',
  regions: 'Regions',
  googleReviewLink: 'GoogleLink',
  fbUserID: 'FBUserID',
  fbUserToken: 'FBUserToken',
  fbLongLivedToken: 'FBLongLivedToken',
  disqlRsns: 'DisqlRsns',
  allowSecConns: 'AllowSecConns',
  allowDupInq: 'AllowDupInq',
  wdConfig: 'WDConfig'
};
// Gateway: resolves the currently logged-in agency's ID from the Jarvis auth cookie.
// Kept local (not imported from AuthUtils) to avoid a circular import with Storage.js.
export const getActiveAgencyID = () => {
  try {
    const jarvisData = localStorage.getItem('Jarvis');
    if (jarvisData) {
      const parsed = JSON.parse(jarvisData);
      return parsed?.[0]?.AgencyID ?? null;
    }
  } catch (error) {
    console.error('Error resolving active agency ID:', error);
  }
  return null;
};

// Gateway: given a localStorage array that may hold rows for multiple agencies
// (B/C/D etc. now store one row per agency the user has ever logged into),
// return only the row belonging to the currently active agency.
const pickActiveAgencyRow = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return undefined;
  const activeAgencyID = getActiveAgencyID();
  if (activeAgencyID != null) {
    // Most rows (B/C etc.) carry AgencyID directly, but NODE_DATA ('D') rows key
    // by NodeID/ActiveID instead (NodeID === the agency's ID for that row).
    const match = rows.find(row => {
      const rowID = row?.AgencyID ?? row?.NodeID ?? row?.ActiveID;
      return Number(rowID) === Number(activeAgencyID);
    });
    if (match) return match;
  }
  return rows[0];
};

// Get localStorage value by key name
export const getCookie = (cname) => {
  return localStorage.getItem(cname);
};

// Set localStorage value with key-value pair
export const setCookie = (cname, cvalue) => {
  localStorage.setItem(cname, cvalue);
};

// Remove localStorage value by key name
export const removeCookie = (cname) => {
  localStorage.removeItem(cname);
};

// Get configuration data from localStorage with default values
export const getConfigData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CONFIG_DATA);
    const data = JSON.parse(raw || '[]');
    const configObj = pickActiveAgencyRow(data) || {};

    const defaultConfig = {
      Products: '[]',
      Sources: '',
      Cities: '',
      ConnFields: '[]',
      RejRsns: '',
      InactRsns: '',
      Segments: '',
      TaskTypes: '',
      ThreadTypes: '[]',    // ← Add this
      RecTypes: '[]',       // ← Add this
      ThreadFieldPrefs: '[]', // ← Add this if missing
      WDConfig: '[]',
      ThreadStages: '[]',
      ConnColPrefs: '[]',
      ThrColPrefs: '[]'
    };

    return { ...defaultConfig, ...configObj };
  } catch (err) {
    console.error("Failed to parse CONFIG_DATA", err);
    return {
      Products: '[]',
      Sources: '',
      Cities: '',
      ConnFields: '[]',
      RejRsns: '',
      InactRsns: '',
      Segments: '',
      TaskTypes: '',
      ThreadTypes: '[]',    // ← Add this
      RecTypes: '[]',       // ← Add this
      ThreadFieldPrefs: '[]', // ← Add this if missing
      WDConfig: '[]',
      ThreadStages: '[]',
      ConnColPrefs: '[]',
      ThrColPrefs: '[]'
    };
  }
};

// Get specific master data from config and handle all data formats properly
// Updated getMasterDataFromConfig function
export const getMasterDataFromConfig = (masterKey, getFullConnFields = false) => {
  try {
    const configData = getConfigData();

    const rawData = configData[masterKey];

    if (Array.isArray(rawData)) {
      return rawData;
    }

    if (typeof rawData === 'number' || typeof rawData === 'boolean') {
      return rawData
    }

    if (typeof rawData === 'string') {
      if (rawData.startsWith('[') && rawData.endsWith(']')) {
        const parsed = JSON.parse(rawData);

        if (masterKey === 'ConnFields' && parsed.length > 0 && parsed[0].F && !getFullConnFields) {
          return parsed.filter(item => !item?.IsD).map(item => item.F);
        }

        if (masterKey === 'ConnFields' && parsed.length > 0 && parsed[0].F && getFullConnFields) {
          return parsed.filter(item => !item?.IsD).map(item => item);
        }

        if (masterKey === 'ThrFields' && parsed.length > 0) {
          return parsed.filter(item => !item?.IsD).map(item => item);
        }

        if (masterKey === 'Regions' && parsed.length > 0) {
          return parsed.filter(item => !item?.IsD).map(item => item);
        }

        if (masterKey === 'ThreadTypes' && Array.isArray(parsed)) {
          return parsed.map(thread => {
            const formatName = (name) => {
              if (name === "Order") return "Sale";
              if (name === "Orders") return "Sales";
              if (name === "order") return "sale";
              if (name === "orders") return "sales";
              return name;
            };
            return {
              ...thread,
              Typ: formatName(thread.Typ),
              TypSng: formatName(thread.TypSng),
              SnSng: formatName(thread.SnSng),
              Sn: formatName(thread.Sn)
            };
          });
        }
        return parsed;
      }

      if (rawData.includes(',')) {
        return rawData.split(',')
          .map(item => item.trim())
          .filter(item =>
            item &&
            item !== '[]' &&
            item !== '""' &&
            item !== 'null' &&
            item !== 'undefined' &&
            item !== '00'  // ← Filter out "00" entries
          );
      }

      if (rawData.trim() && rawData !== '""' && rawData !== '[]' && rawData !== '00') {
        return [rawData.trim()];
      }
    }

    return [];
  } catch (error) {
    console.error(`Error parsing ${masterKey} from config:`, error);
    return [];
  }
};

// Update specific master data in config and handle different storage formats
export const updateMasterDataInConfig = (masterKey, dataArray) => {
  try {
    const configData = getConfigData();

    if (masterKey === 'Products' || masterKey === 'ConnFields' || masterKey === 'ThrFields') {
      configData[masterKey] = JSON.stringify(dataArray);
    } else if (['TaskTypes', 'Sources', 'Segments'].includes(masterKey)) {
      configData[masterKey] = dataArray.join(',');
    } else {
      configData[masterKey] = dataArray.length > 0 ? dataArray.join(',') : '';
      console.log(`Updated ${masterKey} with all items:`, dataArray, '-> stored as:', configData[masterKey]);
    }

    setStorageData(STORAGE_KEYS.CONFIG_DATA, configData);
  } catch (error) {
    console.error(`Error updating ${masterKey} in config:`, error);
  }
};

// Store data in localStorage with proper JSON formatting
export const setStorageData = (key, value) => {

  try {
    if (Array.isArray(value)) {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      localStorage.setItem(key, JSON.stringify([value]));
    }
  } catch (error) {
    console.error(`Error storing data for key ${key}:`, error);
  }
};

// Get storage data and parse it from localStorage
export const getStorageData = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.error(`Error retrieving data for key ${key}:`, error);
    return [];
  }
};

// Add new item to master data with proper format handling
export const addMasterDataItem = (masterKey, item) => {
  try {
    // Pass true if ConnFields to get the full objects for ID calculation
    const currentData = getMasterDataFromConfig(masterKey, masterKey === 'ConnFields');

    if (masterKey === 'ConnFields') {
      const newField = {
        FID: Math.max(...currentData.map(f => f.FID || 0), 0) + 1,
        F: item,
        CDT: new Date().toISOString().slice(0, 19).replace('T', 'T'),
        CUID: 151,
        IsD: 0
      };

      const configData = getConfigData();
      const fullConnFields = JSON.parse(configData.ConnFields || '[]');
      fullConnFields.push(newField);
      updateMasterDataInConfig(masterKey, fullConnFields);

      return getMasterDataFromConfig(masterKey);
    } else {
      const updatedData = [...currentData, item];
      updateMasterDataInConfig(masterKey, updatedData);

      return updatedData;
    }
  } catch (error) {
    console.error(`Error adding item to ${masterKey}:`, error);
    return getMasterDataFromConfig(masterKey);
  }
};

// Remove item from master data with proper format handling
export const removeMasterDataItem = (masterKey, index) => {
  try {
    if (masterKey === 'ConnFields') {
      const configData = getConfigData();
      const fullConnFields = JSON.parse(configData.ConnFields || '[]');
      const displayData = getMasterDataFromConfig(masterKey);

      const fieldName = displayData[index];
      const fieldIndex = fullConnFields.findIndex(f => f.F === fieldName && (f.IsD === 0 || f.IsD === false));

      if (fieldIndex !== -1) {
        fullConnFields[fieldIndex].IsD = 1;
        updateMasterDataInConfig(masterKey, fullConnFields);
      }

      return getMasterDataFromConfig(masterKey);
    } else {
      const currentData = getMasterDataFromConfig(masterKey);
      const updatedData = currentData.filter((_, i) => i !== index);
      updateMasterDataInConfig(masterKey, updatedData);
      return updatedData;
    }
  } catch (error) {
    console.error(`Error removing item from ${masterKey}:`, error);
    return getMasterDataFromConfig(masterKey);
  }
};

// Update specific property in localStorage data based on search criteria
export const changeUpdatePackageCookie = (cookieName, propName, propValue, searchKey, searchValue) => {
  if (cookieName === '' || cookieName === 'H' || propName === '' || searchKey === '') {
    return false;
  }

  try {
    const table = getCookie(cookieName);
    if (table) {
      const rows = JSON.parse(table);
      if (rows && rows.length > 0 && searchKey in rows[0]) {
        const elementIndex = rows.findIndex(element => element[searchKey] == searchValue);
        if (elementIndex !== -1) {
          rows[elementIndex][propName] = propValue;
          setCookie(cookieName, JSON.stringify(rows));
          return true;
        }
      }
    }
  } catch (error) {
    console.error('Error updating package cookie:', error);
  }

  return false;
};

// Clear all localStorage data completely
export const clearAllStorage = () => {
  localStorage.clear();
};

// Get storage value with default fallback option
export const getStorageValue = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.error(`Error getting storage value for key ${key}:`, error);
    return defaultValue;
  }
};

// Set storage value with JSON stringification
export const setStorageValue = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting storage value for key ${key}:`, error);
  }
};

// Check if localStorage item exists or not
export const hasStorageItem = (key) => {
  return localStorage.getItem(key) !== null;
};

// Get all localStorage keys matching specific pattern
export const getStorageKeys = (pattern = null) => {
  const keys = Object.keys(localStorage);
  if (pattern) {
    return keys.filter(key => key.includes(pattern));
  }
  return keys;
};

// Initialize master data with default values if not present
export const initializeMasterData = () => {
  const configData = getConfigData();
  let updated = false;

  Object.values(MASTER_DATA_KEYS).forEach(key => {
    if (!configData[key]) {
      if (key === 'Products' || key === 'ConnFields') {
        configData[key] = '[]';
      } else {
        configData[key] = '';
      }
      updated = true;
    }
  });

  if (updated) {
    setStorageData(STORAGE_KEYS.CONFIG_DATA, configData);
  }
};

export const getConnFieldPrefsFromConfig = () => {
  try {
    const configData = getConfigData();

    if (!configData.ConnFieldPrefs) {
      return getDefaultConnFieldPrefs();
    }

    const rawData = configData.ConnFieldPrefs;

    if (typeof rawData === 'string') {
      const parsed = JSON.parse(rawData);

      // Return first object if it's an array, otherwise return the object
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      } else if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    }

    if (typeof rawData === 'object' && !Array.isArray(rawData)) {
      return rawData;
    }

    return getDefaultConnFieldPrefs();
  } catch (error) {
    console.error('Error parsing ConnFieldPrefs from config:', error);
    return getDefaultConnFieldPrefs();
  }
};

// Get default connection field preferences
export const getDefaultConnFieldPrefs = () => ({
  City: false,
  State: false,
  Country: false,
  TaxID: false,
  Potential: false,
  Source: false,
  Since: false,
  Segement: false,
  POC: false,
  nextTask: false,
  Notes: false,
  Tags: false,
  Email: false
});
export const updateConnFieldPrefsInConfig = (prefsData) => {
  try {
    const configData = getConfigData();

    // Store as JSON string array format to match your existing data structure
    configData.ConnFieldPrefs = JSON.stringify([prefsData]);

    setStorageData(STORAGE_KEYS.CONFIG_DATA, configData);
    console.log('Updated ConnFieldPrefs in localStorage:', prefsData);
    return true;
  } catch (error) {
    console.error('Error updating ConnFieldPrefs in config:', error);
    return false;
  }
};

// Update specific field preference
export const updateSingleConnFieldPref = (fieldName, value) => {
  try {
    const currentPrefs = getConnFieldPrefsFromConfig();
    const updatedPrefs = {
      ...currentPrefs,
      [fieldName]: value
    };

    return updateConnFieldPrefsInConfig(updatedPrefs);
  } catch (error) {
    console.error(`Error updating ${fieldName} preference:`, error);
    return false;
  }
};

// Initialize connection field preferences if not present
export const initializeConnFieldPrefs = () => {
  const configData = getConfigData();

  if (!configData.ConnFieldPrefs) {
    console.log('Initializing ConnFieldPrefs with default values');
    const defaultPrefs = getDefaultConnFieldPrefs();
    updateConnFieldPrefsInConfig(defaultPrefs);
    return defaultPrefs;
  }

  return getConnFieldPrefsFromConfig();
};

export const getAgencyData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AGENCY_DATA);
    const data = JSON.parse(raw || '[]');

    // Return the active agency's object (or the first, as a fallback)
    const activeRow = pickActiveAgencyRow(data);
    if (activeRow) {
      return activeRow;
    }
  } catch (error) {
    console.error('Error parsing agency data:', error);
  }
  return { CountryID: 1 };
};

// Function which will use to get actives data
export const getActiveData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.NODE_DATA);
    const data = JSON.parse(raw || '[]');

    // Return the active agency's row (or the first, as a fallback)
    const activeRow = pickActiveAgencyRow(data);
    if (activeRow) {
      return activeRow;
    }
  } catch (error) {
    console.error('Error parsing agency data:', error);
  }
  return {};
};

// Function which will use to set active data (merges into the other agencies' rows instead of replacing them)
export const setActiveData = (activeData) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.NODE_DATA);
    const rows = JSON.parse(raw || '[]');
    const existingIndex = Array.isArray(rows)
      ? rows.findIndex(row => Number(row?.AgencyID) === Number(activeData?.AgencyID))
      : -1;
    const updatedRows = Array.isArray(rows) ? [...rows] : [];
    if (existingIndex !== -1) {
      updatedRows[existingIndex] = activeData;
    } else {
      updatedRows.push(activeData);
    }
    setStorageData(STORAGE_KEYS.NODE_DATA, updatedRows);
    return true;
  } catch (error) {
    console.error('Error setting agency data:', error);
    return false;
  }
};

// Function which will use to get users data
export const getUserData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    const data = JSON.parse(raw || '[]');

    // Return the first user object or default values
    if (data && data.length > 0) {
      return data;
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  return [];
};

// Function which will set user data
export const setUserData = (userData, isReset) => {
  let usersData = getUserData() || [];

  if (!isReset) {
    userData.forEach((newUser) => {
      // If IsRemove = 1 → remove user from usersData
      if (newUser.IsRemove === 1) {
        usersData = usersData.filter((user) => user.UserID !== newUser.UserID);
      } else {
        // Otherwise, update or insert
        const existingIndex = usersData.findIndex(
          (user) => user.UserID === newUser.UserID
        );

        if (existingIndex !== -1) {
          usersData[existingIndex] = newUser; // update
        } else {
          usersData.push(newUser); // insert
        }
      }
    });
  }
  else {
    usersData = userData;
  }

  try {
    setStorageData(STORAGE_KEYS.USER_DATA, usersData);
    return true;
  } catch (error) {
    console.error("Error setting user data:", error);
    return false;
  }
};

// Merges into the other agencies' rows instead of replacing them
export const setAgencyData = (agencyData) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AGENCY_DATA);
    const rows = JSON.parse(raw || '[]');
    const existingIndex = Array.isArray(rows)
      ? rows.findIndex(row => Number(row?.AgencyID) === Number(agencyData?.AgencyID))
      : -1;
    const updatedRows = Array.isArray(rows) ? [...rows] : [];
    if (existingIndex !== -1) {
      updatedRows[existingIndex] = agencyData;
    } else {
      updatedRows.push(agencyData);
    }
    setStorageData(STORAGE_KEYS.AGENCY_DATA, updatedRows);
    return true;
  } catch (error) {
    console.error('Error setting agency data:', error);
    return false;
  }
};


export const getSCRMConfigs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SCRM_CONFIGS);
    const data = JSON.parse(raw || '[]');
    return data;
  } catch (err) {
    console.error("Failed to parse SCRMConfigs", err);
    return [];
  }
};

export const setSCRMConfigs = (incomingConfigs, isReset = false) => {
  try {
    if (!Array.isArray(incomingConfigs)) return false;
    if (isReset) {
      setStorageData(STORAGE_KEYS.SCRM_CONFIGS, incomingConfigs);
      return true;
    }

    let currentConfigs = getSCRMConfigs();
    if (!Array.isArray(currentConfigs)) currentConfigs = [];

    incomingConfigs.forEach((incoming) => {
      const existingIndex = currentConfigs.findIndex(
        (c) => (incoming.Config && c.Config === incoming.Config) ||
          (incoming.NodeID && (c.NodeID === incoming.NodeID || c.ConfigID === incoming.NodeID))
      );
      if (existingIndex > -1) {
        currentConfigs[existingIndex] = { ...currentConfigs[existingIndex], ...incoming };
      } else {
        currentConfigs.push(incoming);
      }
    });

    setStorageData(STORAGE_KEYS.SCRM_CONFIGS, currentConfigs);
    return true;
  } catch (error) {
    console.error('Error setting SCRMConfigs:', error);
    return false;
  }
};

// Get CntTypewiseWebstr from SCRM configs
export const getCntTypewiseWebstrFromSCRM = () => {
  try {
    const scrmConfigs = getSCRMConfigs();
    const configItem = scrmConfigs.find(config => config.Config === 'CntTypewiseWebstr');

    if (configItem && configItem.Val) {
      const data = typeof configItem.Val === 'string' ? JSON.parse(configItem.Val) : configItem.Val;
      return Array.isArray(data) ? data : [];
    }

    return [];
  } catch (error) {
    console.error('Error parsing CntTypewiseWebstr from SCRM configs:', error);
    return [];
  }
};

// Get transaction types from SCRM configs
export const getTransactionTypesFromSCRM = () => {
  try {
    const scrmConfigs = getSCRMConfigs();
    const xnTypesConfig = scrmConfigs.find(config => config.Config === 'XnTypes');

    if (xnTypesConfig && xnTypesConfig.Val) {
      const transactionTypes = JSON.parse(xnTypesConfig.Val);
      return transactionTypes.map(type => ({
        TID: type.XnTID,
        name: type.LblS,
        title: type?.LblF || '',
        type: type.Typ,
        connectionType: type.CTyp,
        icon: type.Icon || ''
      }));
    }

    return [];
  } catch (error) {
    console.error('Error parsing transaction types from SCRM configs:', error);
    return [];
  }
};

// LeadInfo cookie and localStorage management
export const getLeadInfo = () => {
  try {
    const localVal = getStorageValue('LeadInfo');
    if (localVal && typeof localVal === 'object') return localVal;

    // Fallback: check document.cookie directly
    if (typeof document !== 'undefined') {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; LeadInfo=`);
      if (parts.length === 2) {
        const rawCookie = decodeURIComponent(parts.pop().split(';').shift());
        if (rawCookie) {
          const parsed = JSON.parse(rawCookie);
          if (parsed && typeof parsed === 'object') {
            try {
              localStorage.setItem('LeadInfo', JSON.stringify(parsed));
            } catch (e) { }
            return parsed;
          }
        }
      }
    }
  } catch (e) {
    console.error('Error getting LeadInfo:', e);
  }
  return null;
};

export const setLeadInfo = (info) => {
  if (!info || typeof info !== 'object') return;
  try {
    const cleanInfo = {
      name: (info.name || '').trim(),
      mobile: (info.mobile || '').trim(),
      cc: String(info.cc || '91').replace(/\D/g, '') || '91',
      company: (info.company || '').trim(),
      email: (info.email || '').trim(),
    };
    const jsonStr = JSON.stringify(cleanInfo);
    try {
      localStorage.setItem('LeadInfo', jsonStr);
    } catch (e) { }

    // Also persist as cookie (365 days)
    if (typeof document !== 'undefined') {
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `LeadInfo=${encodeURIComponent(jsonStr)}; expires=${expires}; path=/`;
    }
  } catch (e) {
    console.error('Error setting LeadInfo:', e);
  }
};

export const getJarvisUserData = (targetAgencyID) => {
  try {
    let raw = localStorage.getItem('Jarvis');
    if (!raw && typeof document !== 'undefined') {
      const tempCookie = (`; ${document.cookie}`).split('; tempJarvis=').pop().split(';').shift();
      const jarvisCookie = (`; ${document.cookie}`).split('; Jarvis=').pop().split(';').shift();
      const cookieVal = tempCookie || jarvisCookie;
      if (cookieVal) {
        raw = decodeURIComponent(cookieVal);
      }
    }
    if (raw) {
      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch {
        try {
          parsed = JSON.parse(decodeURIComponent(raw));
        } catch {
          parsed = null;
        }
      }
      if (parsed) {
        if (Array.isArray(parsed)) {
          if (targetAgencyID) {
            const match = parsed.find(item => String(item?.AgencyID) === String(targetAgencyID));
            if (match) return match;
          }
          return parsed[0] || null;
        }
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error getting Jarvis user data:', error);
  }
  return null;
};

// Priority fallback: LeadInfo -> Jarvis (all necessary user & agency details are in Jarvis)
export const getCustomerInfo = (targetAgencyID) => {
  const lead = getLeadInfo();
  const jarvis = getJarvisUserData(targetAgencyID);

  const rawCC = lead?.cc || jarvis?.CC || jarvis?.cc || '91';
  return {
    name: (lead?.name || jarvis?.Name || jarvis?.UserName || '').trim(),
    mobile: (lead?.mobile || jarvis?.Mobile || jarvis?.ContactNo || '').trim(),
    cc: String(rawCC).replace(/\D/g, '') || '91',
    company: (lead?.company || jarvis?.AgencyName || jarvis?.CompanyName || '').trim(),
    email: (lead?.email || jarvis?.Email || jarvis?.email || '').trim(),
  };
};

export { setLeadInfo as saveLeadInfo };
export { getCookie as getLocalStorage };
export { setCookie as setLocalStorage };
export { removeCookie as removeLocalStorage };